import type { ErrorHandler, RawLocation, Route } from 'vue-router/types/router';

import Vue from 'vue';
import VueRouter, { isNavigationFailure, NavigationFailureType } from 'vue-router';

import routes from './routes';

import { useProfileStore } from '@/store/profile';

// vue-router не даёт нам почему-то передавать query через route guard, делаем это
class CustomVueRouter extends VueRouter {
  // @ts-expect-error monkey patching
  push(to: RawLocation, onComplete?: (route: Route) => void, onAbort?: ErrorHandler): void {
    try {
      super.push(to, onComplete, onAbort);
    }
    catch (exception) {
      if (import.meta.env.DEV === false) {
        if (isNavigationFailure(exception, NavigationFailureType.duplicated))
          return;
      }

      throw exception;
    }
  }

  async replace(to: RawLocation): Promise<Route> {
    return super.replace(to).catch((exception) => {
      if (import.meta.env.DEV === false) {
        if (isNavigationFailure(exception, NavigationFailureType.duplicated))
          return {} as Route;
      }

      throw exception;
    });
  }
}

if (window.location.search.length) {
  // не совладал с тем как vue-router управляет query - храним utm в другом месте
  const initialSearchParams = new URLSearchParams(window.location.search);
  for (const [key, value] of initialSearchParams) {
    if (key.startsWith('utm_') || key === 'ref') {
      localStorage.setItem(key, value);
      document.cookie = [
        `${key}=${encodeURIComponent(value)}`,
        'Secure',
        import.meta.env.PROD && `Domain=${import.meta.env.VITE_APP_COOKIE_DOMAIN}`,
        `Expires=${new Date(Date.now() + (2_678_400 * 1000)).toUTCString()}`,
        `Max-Age=${2_678_400}`,
        'SameSite=lax',
      ]
        .filter((item) => Boolean(item))
        .join('; ');
    }
  }
}

const router = new CustomVueRouter({
  mode: 'history',
  base: import.meta.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    const position = savedPosition ?? { x: 0, y: 0 };
    if (to.path === from.path || to?.meta?.noScroll)
      return;
    return new Promise((resolve) => {
      // ждём полного завершения анимации на странице (если имеется), и делаем скролл
      setTimeout(() => resolve(position), 500);
    });
  },
});

router.afterEach((to) => {
  if (to.meta?.pageTitle !== undefined) {
    // null - сброс на обычное наименование (из dotenv), без упоминания страниц
    Vue.changeTitle(to.meta.pageTitle);
  }
});

router.beforeResolve(async (to, from, next) => {
  if (to.path === '/account/login')
    return next();

  const profileStore = useProfileStore();

  if (to.meta?.role && (profileStore.roleSide !== to.meta.role)) {
    return next({ name: 'board' }); // FIXME: нужна какая-то страница что "доступ на эту страницу запрещен из-за роли"
  }

  if (to.name === 'Cabinet') {
    return next({ name: 'board' });
  }

  // FIXME: создать meta свойство что-то вроде "requireSelectedCompany"
  if (to.name === 'board' && !profileStore.companyId) {
    return next({ name: 'pickMemberRole' });
  }

  if (profileStore.isAuthenticated) {
    if (profileStore.roleSide === 'borrower') {
      const toRegisterBorrowerRoute = to.name === 'borrowerRegistration' || to.name === 'pickMemberRole' || to.name === 'setCode';
      if (profileStore.borrowerAppRegState && profileStore.borrowerAppRegState !== 'done') {
        if (!toRegisterBorrowerRoute) {
          return next({ name: 'borrowerRegistration' });
        }
      }
      else if (!profileStore.borrowerAppRegState && !toRegisterBorrowerRoute) {
        return next({ name: 'pickMemberRole' });
      }
    }
  }

  return next();
});

router.beforeEach(async (to, from, next) => {
  const profileStore = useProfileStore();

  if (to.path.includes('cabinet') || to.name === 'index')
    await profileStore.fetchServerData().catch(console.error);

  return next();
});

export default router;
export { CustomVueRouter };
